import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import {
    Search,
    Bell,
    ChevronRight,
    Menu,
    X,
    ChevronDown,
    Settings,
    LifeBuoy,
    Moon,
    LogOut
} from "lucide-react";
import {
    logout,
    getWorkspaceLabel,
    getBrandDisplayName,
    getBrandInitials
} from "../../services/authService";

import { globalSearch } from "../../services/crmService";
import "../../styles/nexusTopbar.css";
import { getStoredThemeMode, toggleThemeMode } from "../../utils/themeMode";

function titleCase(value = "") {
    return String(value)
        .replace(/[_-]/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, c => c.toUpperCase());
}

function compactMiddle(value = "", max = 34) {
    const text = String(value || "").trim();

    if (text.length <= max) return text;

    const left = Math.max(12, Math.floor(max * 0.58));
    const right = Math.max(8, max - left - 3);

    return `${text.slice(0, left)}...${text.slice(-right)}`;
}

function buildBreadcrumbs(pathname, params, title) {
    const items = [{ label: "Dashboard", to: "/" }];

    if (pathname.startsWith("/projects")) {
        items.push({ label: "Projects", to: "/projects" });
    } else if (pathname.startsWith("/leads")) {
        items.push({ label: "Leads", to: "/leads" });
    } else if (pathname.startsWith("/kanban")) {
        items.push({ label: "Kanban", to: "/kanban" });
    } else if (pathname.startsWith("/variants")) {
        items.push({ label: "Variants", to: "/variants" });
    } else if (pathname.startsWith("/vertical-seeds")) {
        items.push({ label: "Vertical Seeds", to: "/vertical-seeds" });
    } else if (pathname.startsWith("/accounts")) {
        items.push({ label: "Accounts", to: "/accounts" });
    } else if (pathname.startsWith("/crm/projects")) {
        items.push({ label: "Projects", to: "/projects" });

        if (params.projectId) {
            items.push({
                label: "Project Detail",
                to: `/crm/projects/${params.projectId}`
            });
        }

        if (pathname.includes("/intelligence")) {
            items.push({ label: "Intelligence", to: null });
        } else if (pathname.includes("/branding")) {
            items.push({ label: "Branding", to: null });
        } else if (pathname.includes("/modules")) {
            items.push({ label: "Modules", to: null });

            if (params.moduleKey) {
                items.push({ label: titleCase(params.moduleKey), to: null });
            }
        }
    } else if (pathname.startsWith("/settings")) {
        items.push({ label: "Settings", to: "/settings" });

        if (pathname.includes("/client")) {
            items.push({ label: "Business", to: null });
        } else if (pathname.includes("/crm")) {
            items.push({ label: "CRM & Leads", to: null });
        } else if (pathname.includes("/usage")) {
            items.push({ label: "Usage & Plan", to: null });
        }
    }

    if (items.length === 1 && title && title !== "Dashboard") {
        items.push({ label: title, to: null });
    }

    return items;
}

function getPageKicker(pathname) {
    if (pathname.includes("/modules")) return "Module Studio";
    if (pathname.includes("/crm/projects")) return "Project Workspace";
    if (pathname.includes("/vertical-seeds")) return "Seed Operations";
    if (pathname.includes("/accounts")) return "Account Operations";
    if (pathname.includes("/leads")) return "Lead Pipeline";
    if (pathname.includes("/kanban")) return "Lead Board";
    if (pathname.includes("/settings")) return "Settings";
    if (pathname.includes("/variants")) return "Variant Lab";
    return "Workspace";
}

function getResultIcon(type) {
    const value = String(type || "").toLowerCase();

    if (value === "project") return "🌐";
    if (value === "lead") return "📩";
    if (value === "module") return "🧩";

    return "🔎";
}

export default function Topbar({ title, onMenuClick }) {
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    const desktopSearchRef = useRef(null);
    const mobileSearchRef = useRef(null);
    const userMenuRef = useRef(null);

    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);

    const [themeMode, setThemeMode] = useState(getStoredThemeMode());

    const handleToggleThemeMode = () => {
        const next = toggleThemeMode();
        setThemeMode(next);
    };

    useEffect(() => {
        const sync = () => setThemeMode(getStoredThemeMode());

        window.addEventListener("snx-theme-mode-changed", sync);
        window.addEventListener("storage", sync);

        return () => {
            window.removeEventListener("snx-theme-mode-changed", sync);
            window.removeEventListener("storage", sync);
        };
    }, []);

    const breadcrumbs = useMemo(() => {
        return buildBreadcrumbs(location.pathname, params, title);
    }, [location.pathname, params, title]);

    const compactTitle = compactMiddle(title || "Dashboard", 36);
    const kicker = getPageKicker(location.pathname);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.altKey && e.key.toLowerCase() === "n") {
                e.preventDefault();
                handleToggleThemeMode();
            }
        };

        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, []);

    const openSupport = () => {
        setUserMenuOpen(false);
        window.location.href = "mailto:support@shriansh.ai?subject=Shriansh Nexus Support";
    };

    const workspaceName = getBrandDisplayName();
    const workspaceLabel = getWorkspaceLabel();
    const initials = getBrandInitials();

    useEffect(() => {
        const trimmed = query.trim();

        if (trimmed.length < 2) {
            setResults([]);
            setSearchOpen(false);
            setSearching(false);
            return;
        }

        let cancelled = false;

        const timer = setTimeout(async () => {
            try {
                setSearching(true);

                const data = await globalSearch(trimmed);

                if (cancelled) return;

                setResults(Array.isArray(data) ? data : []);
                setSearchOpen(true);
            } catch (error) {
                console.error(error);

                if (!cancelled) {
                    setResults([]);
                    setSearchOpen(true);
                }
            } finally {
                if (!cancelled) {
                    setSearching(false);
                }
            }
        }, 300);

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [query]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const target = event.target;

            const clickedDesktopSearch =
                desktopSearchRef.current && desktopSearchRef.current.contains(target);

            const clickedMobileSearch =
                mobileSearchRef.current && mobileSearchRef.current.contains(target);

            const clickedUserMenu =
                userMenuRef.current && userMenuRef.current.contains(target);

            if (!clickedDesktopSearch && !clickedMobileSearch) {
                setSearchOpen(false);
            }

            if (!clickedUserMenu) {
                setUserMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setMobileSearchOpen(false);
        setSearchOpen(false);
        setUserMenuOpen(false);
        setQuery("");
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        window.location.replace("/login");
    };

    const handleResultClick = (item) => {
        if (!item?.url) return;

        setQuery("");
        setResults([]);
        setSearchOpen(false);
        setMobileSearchOpen(false);

        navigate(item.url);
    };

    const handleSearchKeyDown = (event) => {
        if (event.key === "Enter" && results.length > 0) {
            event.preventDefault();
            handleResultClick(results[0]);
        }

        if (event.key === "Escape") {
            setSearchOpen(false);
            setMobileSearchOpen(false);
        }
    };

    const goToSettings = () => {
        setUserMenuOpen(false);
        navigate("/settings");
    };

    const renderSearchResults = (isMobile = false) => {
        if (!searchOpen) return null;

        return (
            <div className={`snx-topbar-search-results ${isMobile ? "mobile" : ""}`}>
                {searching ? (
                    <div className="snx-topbar-search-empty">Searching...</div>
                ) : results.length === 0 ? (
                    <div className="snx-topbar-search-empty">No results found.</div>
                ) : (
                    results.map((item, index) => (
                        <button
                            type="button"
                            key={`${item.type}-${item.title}-${index}`}
                            className="snx-topbar-search-result"
                            onClick={() => handleResultClick(item)}
                        >
                            <span className="snx-topbar-search-result-icon">
                                {getResultIcon(item.type)}
                            </span>

                            <span className="snx-topbar-search-result-main">
                                <strong>{item.title}</strong>
                                <small>{item.subtitle || item.type}</small>
                            </span>

                            {item.badge && (
                                <span className="snx-topbar-search-result-badge">
                                    {item.badge}
                                </span>
                            )}
                        </button>
                    ))
                )}
            </div>
        );
    };

    return (
        <header className="snx-topbar">
            <div className="snx-topbar-row">
                <button
                    type="button"
                    className="snx-topbar-menu-btn"
                    aria-label="Open navigation"
                    onClick={onMenuClick}
                >
                    <Menu size={20} />
                </button>

                <div className="snx-topbar-title">
                    <span className="snx-topbar-kicker">{kicker}</span>
                    <h1 title={title}>{compactTitle}</h1>
                </div>

                <div className="snx-topbar-actions">
                    <div className="snx-topbar-search-wrap" ref={desktopSearchRef}>
                        <div className="snx-topbar-search">
                            <Search size={18} />

                            <input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onFocus={() => {
                                    if (query.trim().length >= 2) {
                                        setSearchOpen(true);
                                    }
                                }}
                                onKeyDown={handleSearchKeyDown}
                                placeholder="Search projects, leads, modules..."
                            />
                        </div>

                        {renderSearchResults(false)}
                    </div>

                    <button
                        type="button"
                        className="snx-topbar-icon-btn snx-topbar-mobile-search-btn"
                        title="Search"
                        onClick={() => setMobileSearchOpen(value => !value)}
                    >
                        {mobileSearchOpen ? <X size={18} /> : <Search size={18} />}
                    </button>

                    <button
                        className="snx-topbar-icon-btn snx-topbar-bell-btn"
                        type="button"
                        title="Notifications"
                    >
                        <Bell size={18} />
                    </button>

                    <div className="snx-topbar-account" ref={userMenuRef}>
                        <button
                            type="button"
                            className="snx-topbar-account-trigger"
                            onClick={() => setUserMenuOpen(value => !value)}
                            aria-haspopup="menu"
                            aria-expanded={userMenuOpen}
                            title={workspaceName}
                        >
                            <span className="snx-topbar-avatar">{initials || "U"}</span>

                            <span className="snx-topbar-account-copy">
                                <strong>{workspaceName}</strong>
                                <small>{workspaceLabel}</small>
                            </span>

                            <ChevronDown
                                size={16}
                                className={`snx-topbar-account-chevron ${userMenuOpen ? "open" : ""}`}
                            />
                        </button>

                        {userMenuOpen && (
                            <div className="snx-topbar-account-menu" role="menu">
                                <button type="button" onClick={handleToggleThemeMode} className="user-menu-item">
                                    <Moon size={20} />
                                    <span>{themeMode === "dark" ? "Light Mode" : "Night Mode"}</span>
                                    <small>Alt+N</small>
                                </button>

                                <button type="button" role="menuitem" onClick={goToSettings} className="user-menu-item">
                                    <Settings size={17} />
                                    <span>Settings</span>
                                </button>

                                <button type="button" onClick={openSupport} className="user-menu-item">
                                    <LifeBuoy size={20} />
                                    <span>Support</span>
                                </button>

                                <div className="snx-topbar-menu-divider" />

                                <button
                                    type="button"
                                    role="menuitem"
                                    className="danger"
                                    onClick={handleLogout}
                                >
                                    <LogOut size={17} />
                                    <span>Sign Out</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {mobileSearchOpen && (
                <div className="snx-topbar-mobile-search-panel">
                    <div className="snx-topbar-search-wrap" ref={mobileSearchRef}>
                        <div className="snx-topbar-search">
                            <Search size={18} />

                            <input
                                autoFocus
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                onKeyDown={handleSearchKeyDown}
                                placeholder="Search projects, leads, modules..."
                            />
                        </div>

                        {renderSearchResults(true)}
                    </div>
                </div>
            )}

            <nav className="snx-topbar-breadcrumbs" aria-label="Breadcrumb">
                {breadcrumbs.map((item, index) => {
                    const isLast = index === breadcrumbs.length - 1 || !item.to;
                    const label = isLast
                        ? compactMiddle(item.label, 28)
                        : compactMiddle(item.label, 22);

                    return (
                        <span key={`${item.label}-${index}`} className="snx-topbar-breadcrumb-item">
                            {index > 0 && <ChevronRight size={14} />}

                            {isLast ? (
                                <strong title={item.label}>{label}</strong>
                            ) : (
                                <Link title={item.label} to={item.to}>
                                    {label}
                                </Link>
                            )}
                        </span>
                    );
                })}
            </nav>
        </header>
    );
}